"use client";

import { db } from "@utils/IndexedDb";
import classNames from "classnames";
import { useLiveQuery } from "dexie-react-hooks";
import { AnimatePresence, motion, useMotionValueEvent, useScroll } from "framer-motion";
import { useLocale } from "next-intl";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { FC, useEffect, useMemo, useState } from "react";
import { Tab, TabList, TabPanel, Tabs } from "react-tabs";

import AutogeneratedCards from "@/components/AutogeneratedCarousel/AutogeneratedCards";
import { SEARCH_PAGE, SEO_SALE_PAGE, cleanParams } from "@/components/ui/Form/Filters/FilterUtils";
import useBreakPoint from "@/hooks/useBreakpoint";
import useIsSaleScreen from "@/hooks/useIsSaleScreen";
import { AutogeneratedCarouselCard, AutogeneratedCarouselCardGroup, AutogeneratedCarouselEntry, AutogeneratedCarouselProps, CardType } from "@/types";
import { Community, PRODUCT_TYPE, VISUAL_VARIANT } from "@/types";

import SearchResult from "../SearchResult/SearchResult";
import FilterBar from "../ui/Form/Filters/FilterBar";

const showTabs = (groups: AutogeneratedCarouselCardGroup[], type: string): boolean => {
  let res = false;
  switch (type) {
    case CardType.EDITORIAL || type !== CardType.ARTICLE:
      res = false;
      break;
    default:
      res = groups?.length > 1;
      break;
  }
  return res;
};

function getStructuredData(entry: AutogeneratedCarouselEntry) {
  if (typeof window !== "undefined") {
    let cards: AutogeneratedCarouselCard[] = [];
    entry?.groups?.forEach((group) => {
      group?.cards?.forEach((card) => {
        cards?.push(card);
      });
    });

    if (entry?.type === CardType["UNIT-TYPE"] && cards?.length > 0) {
      return {
        "@context": "http://schema.org",
        "@type": "ItemList",
        itemListElement: cards.map((card, index) => {
          return {
            "@type": "ListItem",
            position: index + 1,
            name: card.title,
            url: window?.location?.origin + card.cta?.link?.url,
          };
        }),
      };
    } else if (entry.type === CardType.PROJECT && cards.length > 0) {
      return {
        "@context": "http://schema.org",
        "@type": "ItemList",
        itemListElement: cards.map((card, index) => {
          return {
            "@type": "ListItem",
            position: index + 1,
            name: card.title,
            url: window?.location?.origin + card.cta?.link?.url,
          };
        }),
      };
    }
  }
  return false;
}

const AutogeneratedCarousel: FC<AutogeneratedCarouselProps> = ({ entry, serverSideCookieCurrency, currencyFromApiRes, communitiesData }) => {
  const pathname = usePathname();
  const { isMd } = useBreakPoint("md");
  const [structuredData, setStructuredData] = useState<any>();
  useEffect(() => {
    setStructuredData(getStructuredData(entry));
  }, [entry]);

  const isSo = useIsSaleScreen();
  const locale = useLocale();

  const propertyTypeRecords: number | undefined = useLiveQuery(async () => {
    const projectCount = await db.PROPERTY_TYPE.count();
    return projectCount || 0;
  });

  const isHomePage = pathname === "/" || pathname === `/${locale}/`;
  if (isSo && isHomePage) {
    return null;
  }

  return (
    <>
      <section
        className={classNames("flex flex-col md:items-end md:justify-end", { "mx-auto w-22\\/24 md:w-20\\/24": entry.showAsCardList })}
        id={entry.type === CardType["UNIT-TYPE"] ? "APL-component" : ""}
      >
        {entry?.title && entry?.title !== "" ? (
          <div className="w-full">
            <div
              className={`flex items-center ${entry?.variant ? "ml-m md:ml-[calc(100%_*_2_/_24)]" : entry.showAsCardList ? "" : "justify-center"} gap-l md:justify-normal ${entry?.variant ? "md:w-4/5" : "w-full"}`}
            >
              {entry?.typeIndex === 1 ? (
                <h1
                  className={`accent-font accent-title accent-color mt-l font-primary uppercase ${entry?.variant ? "w-3/4" : ""} ${!entry.cta ? "mx-auto text-center" : ""} ${entry.showAsCardList ? "text-m md:text-l md:px-xs" : "md:mx-auto md:text-xl text-l"} md:mb-l md:mt-xxl`}
                >
                  {entry?.title}
                </h1>
              ) : (
                <h2
                  className={`accent-font accent-title accent-color mt-l font-primary uppercase ${entry?.variant ? "w-3/4" : ""} ${!entry.cta ? "mx-auto text-center" : ""} ${entry.showAsCardList ? "text-m md:text-l md:px-xs" : "md:mx-auto md:text-xl text-l"} md:mb-l md:mt-xxl`}
                >
                  {entry?.title}
                </h2>
              )}

              {entry?.variant && entry?.cta?.link?.url && (
                <Link href={entry.cta.link.url} className="ml-auto mt-3xl text-s font-medium underline underline-offset-4 md:mb-l md:mt-xxl">
                  {entry.cta.label}
                </Link>
              )}
            </div>
          </div>
        ) : null}
        {!entry.hideFilter && pathname?.includes("for-sale/") && (
          <div className="w-full">
            {/* <SearchResult seoSalesPages={true} /> */}
            <div
              className={`sticky top-0 md:top-[var(--header-height)]  ${isMd ? "h-3xl" : "mt-3xl"}  z-30 bg-white border-b border-b-gray-10 flex flex-col justify-center`}
            >
              <AnimatePresence>
                {!!propertyTypeRecords && propertyTypeRecords > 0 && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="container">
                    <FilterBar
                      name={SEO_SALE_PAGE}
                      filters={[PRODUCT_TYPE.PROPERTY_TYPE, "bedrooms", "price"]}
                      variant={VISUAL_VARIANT.WHITE}
                      isAutoGeneratedCarousel={true}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        )}
        <Tabs className="w-full md:mx-auto" focusTabOnClick={false}>
          {showTabs(entry.groups ?? [], entry?.type) && <TabList>{entry?.groups?.map((tab, index) => <Tab key={index}>{tab?.label}</Tab>)}</TabList>}
          <div className="w-full md:mx-auto md:mr-0 md:pr-0">
            <div className="mb-xl mt-l md:mt-m">
              {entry?.groups?.map((tab, index) =>
                entry?.groups && entry?.groups?.length > 1 ? (
                  <TabPanel key={index}>
                    <AutogeneratedCards
                      id={entry.id}
                      cardType={entry.type}
                      cards={tab.cards}
                      cta={entry.cta}
                      variant={entry.variant}
                      highlightProjectTag={tab.highlightProjectTag}
                      showAsCardList={entry.showAsCardList}
                      serverSideCookieCurrency={serverSideCookieCurrency}
                      currencyFromApiRes={currencyFromApiRes}
                      communitiesData={communitiesData}
                    />
                  </TabPanel>
                ) : (
                  <AutogeneratedCards
                    id={entry.id}
                    key={index}
                    cardType={entry.type}
                    cards={tab.cards}
                    cta={entry.cta}
                    variant={entry.variant}
                    highlightProjectTag={tab.highlightProjectTag}
                    showAsCardList={entry.showAsCardList}
                    serverSideCookieCurrency={serverSideCookieCurrency}
                    currencyFromApiRes={currencyFromApiRes}
                    communitiesData={communitiesData}
                  />
                ),
              )}
            </div>
          </div>
        </Tabs>
      </section>
      {structuredData && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />}
    </>
  );
};

export default AutogeneratedCarousel;
